import { matchDocumentType, type DocumentTypeDefinition } from './documentTypes';

/** One text run from a PDF page, in PDF coordinates (y grows upward). */
export interface PdfTextItem {
  str: string;
  x: number;
  y: number;
}

export interface DocumentReadResult {
  status: 'recognized' | 'image-only' | 'unknown' | 'incomplete';
  documentTypeLabel: string;
  person: string;
  yearLabel: string;
  revisionLabel: string;
  message: string;
}

const EMPTY_FIELDS = {
  documentTypeLabel: '',
  person: '',
  yearLabel: '',
  revisionLabel: '',
};

const KANJI_ONLY = /^[\u4e00-\u9fff]+$/;
const COMPANY = /株式会社|有限会社|合同会社|国民健康保険/;
const NAME_LABELS = new Set(['氏名', 'フリガナ', '住所', '居所', '支払', '個人番号', '種別', '役職名']);

export function identifyDocument(items: PdfTextItem[], _fileName = ''): DocumentReadResult {
  const meaningful = items
    .map((item) => item.str.replace(/\s/g, ''))
    .join('');
  if (meaningful.replace(/[0-9０-９]/g, '').length < 8) {
    return {
      status: 'image-only',
      ...EMPTY_FIELDS,
      message: 'このPDFは画像のため、見出しを確認しています。',
    };
  }

  const definition = matchDocumentType(meaningful);
  if (!definition) {
    return {
      status: 'unknown',
      ...EMPTY_FIELDS,
      message: '対応していない書類です。',
    };
  }

  const extracted = extractFields(definition, items);
  const missingRequired =
    (definition.id !== 'standard-bonus' && !extracted.person) ||
    (definition.id === 'withholding-slip' && !extracted.yearLabel) ||
    (definition.usesRevisionMonth && !extracted.revisionLabel);

  if (definition.id === 'standard-bonus' || missingRequired) {
    return {
      status: 'incomplete',
      documentTypeLabel: definition.label,
      person: extracted.person,
      yearLabel: extracted.yearLabel,
      revisionLabel: definition.usesRevisionMonth ? extracted.revisionLabel : '',
      message:
        definition.id === 'standard-bonus'
          ? '書類の種類は読み取りました。対象者と年月の読み取りは次のステップで接続します。'
          : '一部の項目を読み取れませんでした。',
    };
  }

  return {
    status: 'recognized',
    documentTypeLabel: definition.label,
    person: extracted.person,
    yearLabel: extracted.yearLabel,
    revisionLabel: definition.usesRevisionMonth ? extracted.revisionLabel : '',
    message: '内容を確認してください。',
  };
}

const REMUNERATION_TITLE = '基準報酬決定通知書';

/**
 * A scan is a 基準報酬決定通知書 only when the page text contains that title.
 * The person and the period still come from 氏名_日付_番号.pdf.
 */
function confirmsRemunerationTitle(compact: string): boolean {
  if (compact.includes(REMUNERATION_TITLE)) return true;
  if (compact.includes('基準賞与')) return false;
  // A scan often splits the title and misreads one character, as in 基準報本決定通知書.
  return /基準.{0,2}決定通知書/.test(compact) && compact.includes('保険料');
}

export function confirmRemunerationScan(ocrText: string, fileName: string): DocumentReadResult {
  const compact = ocrText.normalize('NFKC').replace(/\s/g, '');
  if (!confirmsRemunerationTitle(compact)) {
    return {
      status: 'unknown',
      ...EMPTY_FIELDS,
      message: 'このPDFは基準報酬決定通知書として確認できませんでした。',
    };
  }
  const fromFileName = readRemunerationFileName(fileName);
  if (!fromFileName) {
    return {
      status: 'image-only',
      ...EMPTY_FIELDS,
      documentTypeLabel: REMUNERATION_TITLE,
      message: '基準報酬決定通知書と確認しました。ファイル名は「氏名_日付_番号.pdf」にしてください。',
    };
  }
  return fromFileName;
}

/**
 * Scanned 基準報酬決定通知書 files are named 氏名_日付_番号.pdf.
 * The date is YYYYMMDD, YYYY-MM-DD, or 令和N年M月. The last part is ignored.
 */
export function readRemunerationFileName(fileName: string): DocumentReadResult | null {
  const base = fileName.replace(/^.*[\\/]/, '').replace(/\.pdf$/i, '');
  const parts = base.split('_').filter((part) => part.length > 0);
  if (parts.length < 3) return null;
  if (!/^\d+$/.test(parts[parts.length - 1])) return null;

  const person = normalizeSpaces(parts.slice(0, -2).join('_'));
  const dated = parseFileNameDate(parts[parts.length - 2]);
  if (!person || !dated) return null;

  return {
    status: 'recognized',
    documentTypeLabel: '基準報酬決定通知書',
    person,
    yearLabel: dated.yearLabel,
    revisionLabel: dated.revisionLabel,
    message: '対象者と年月はファイル名から読み取りました。内容を確認してください。',
  };
}

function parseFileNameDate(value: string): { yearLabel: string; revisionLabel: string } | null {
  const era = value.match(/^令和([0-9０-９]{1,2})年([0-9０-９]{1,2})月/);
  if (era) {
    const year = toAsciiDigits(era[1]);
    const month = String(Number(toAsciiDigits(era[2])));
    return { yearLabel: `令和${year}年`, revisionLabel: `令和${year}年${month}月` };
  }

  const compact = value.replace(/-/g, '');
  const western = compact.match(/^(\d{4})(\d{2})(\d{2})?$/);
  if (!western) return null;
  const year = Number(western[1]);
  const month = Number(western[2]);
  if (month < 1 || month > 12 || year < 2019) return null;
  const reiwaYear = year - 2018;
  return { yearLabel: `令和${reiwaYear}年`, revisionLabel: `令和${reiwaYear}年${month}月` };
}

function extractFields(
  definition: DocumentTypeDefinition,
  items: PdfTextItem[]
): { person: string; yearLabel: string; revisionLabel: string } {
  if (definition.id === 'withholding-slip') {
    return {
      person: findWithholdingRecipient(items),
      yearLabel: findEraYear(items),
      revisionLabel: '',
    };
  }
  if (definition.id === 'standard-remuneration') {
    const revisionLabel = findRevisionMonth(items);
    return {
      person: findLabeledPerson(items, '組合員氏名'),
      yearLabel: yearFromRevision(revisionLabel),
      revisionLabel,
    };
  }
  return { person: '', yearLabel: '', revisionLabel: '' };
}

function findEraYear(items: PdfTextItem[]): string {
  const lines = groupLines(items);
  let best = '';
  let bestY = -Infinity;
  lines.forEach((line) => {
    const text = line.items.map((item) => item.str).join('');
    if (!text.includes('令')) return;
    if (text.includes('昭和') && !text.includes('令和') && !/^令/.test(text.replace(/\s/g, ''))) return;
    const digit = digitToTheRightOf(line.items, '令');
    if (!digit) return;
    const year = Number(toAsciiDigits(digit));
    if (!Number.isFinite(year) || year < 1 || year > 99) return;
    if (line.y > bestY) {
      bestY = line.y;
      best = `令和${year}年`;
    }
  });
  return best;
}

function findRevisionMonth(items: PdfTextItem[]): string {
  const compact = items.map((item) => item.str).join('').replace(/\s/g, '');
  const match = compact.match(/適用年月令和?([0-9０-９]{1,2})年([0-9０-９]{1,2})月/);
  if (!match) {
    const loose = compact.match(/令和([0-9０-９]{1,2})年([0-9０-９]{1,2})月/);
    if (!loose) return '';
    return `令和${toAsciiDigits(loose[1])}年${toAsciiDigits(loose[2])}月`;
  }
  return `令和${toAsciiDigits(match[1])}年${toAsciiDigits(match[2])}月`;
}

function yearFromRevision(revisionLabel: string): string {
  const match = revisionLabel.match(/^(令和[0-9]+年)/);
  return match ? match[1] : '';
}

function findWithholdingRecipient(items: PdfTextItem[]): string {
  const lines = groupLines(items);
  const furiganaIndex = lines.findIndex((line) => {
    const text = line.items.map((item) => item.str).join('');
    return text.includes('フリガナ') && /[ｦ-ﾟァ-ヶ]/.test(text);
  });
  if (furiganaIndex >= 0) {
    const following = lines.slice(furiganaIndex + 1, furiganaIndex + 4);
    for (const line of following) {
      const name = personNameFromLine(line.items);
      if (name) return name;
    }
  }

  for (const line of lines) {
    const text = line.items.map((item) => item.str).join('');
    if (COMPANY.test(text) || text.includes('源泉') || text.includes('給与') || text.includes('フリガナ')) {
      continue;
    }
    const name = personNameFromLine(line.items);
    if (name) return name;
  }
  return '';
}

function personNameFromLine(items: PdfTextItem[]): string {
  const parts = items.filter((item) => {
    const text = item.str.replace(/\s/g, '');
    return KANJI_ONLY.test(text) && !NAME_LABELS.has(text);
  });
  const joined = joinNameParts(parts);
  if (joined.length < 2 || COMPANY.test(joined)) return '';
  return joined;
}

function findLabeledPerson(items: PdfTextItem[], label: string): string {
  const lines = groupLines(items);
  for (const line of lines) {
    const text = line.items.map((item) => item.str).join('');
    const compact = text.replace(/\s/g, '');
    const index = compact.indexOf(label);
    if (index < 0) continue;
    const after = compact.slice(index + label.length).replace(/^[：:様\s]*/, '');
    const name = after.match(/^[\u4e00-\u9fff]{1,6}(?:[\s　][\u4e00-\u9fff]{1,6})?/);
    if (name) return normalizeSpaces(name[0]);
  }
  return '';
}

function digitToTheRightOf(
  items: Array<PdfTextItem & { x: number }>,
  marker: string
): string {
  const ordered = [...items].sort((a, b) => a.x - b.x);
  const markerIndex = ordered.findIndex((item) => item.str.includes(marker));
  if (markerIndex < 0) return '';
  const origin = ordered[markerIndex];
  for (const item of ordered.slice(markerIndex + 1)) {
    if (item.x - origin.x > 80) break;
    const digits = toAsciiDigits(item.str).replace(/[^0-9]/g, '');
    if (digits.length >= 1 && digits.length <= 2) return digits;
  }
  const inline = ordered
    .slice(markerIndex)
    .map((item) => item.str)
    .join('');
  const match = inline.match(/令\s*和?\s*([0-9０-９]{1,2})/);
  return match ? toAsciiDigits(match[1]) : '';
}

function joinNameParts(parts: PdfTextItem[]): string {
  if (parts.length === 0) return '';
  let name = '';
  let previous: PdfTextItem | undefined;
  parts.forEach((part) => {
    const text = normalizeSpaces(part.str);
    if (!text) return;
    if (previous) {
      const gap = part.x - previous.x;
      const expected = Math.max(previous.str.replace(/\s/g, '').length, 1) * 12;
      if (gap > expected) name += ' ';
    }
    name += text;
    previous = part;
  });
  return normalizeSpaces(name);
}

function groupLines(items: PdfTextItem[]): Array<{ y: number; items: PdfTextItem[] }> {
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
  const lines: Array<{ y: number; items: PdfTextItem[] }> = [];
  sorted.forEach((item) => {
    const current = lines[lines.length - 1];
    if (!current || Math.abs(current.y - item.y) > 2) {
      lines.push({ y: item.y, items: [item] });
      return;
    }
    current.items.push(item);
  });
  return lines;
}

function normalizeSpaces(value: string): string {
  return value.replace(/[\s　]+/g, ' ').trim();
}

function toAsciiDigits(value: string): string {
  return value.replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xfee0));
}
